import { gql } from "@apollo/client";
import { ApolloClientQuery } from "./_setup";
import { createLogSession } from "../restful/logs/logs";

const GET_USER_AUTH_PROVIDERS = gql`
    query getUserAuthProviders {
        getUserAuthProviders {
            ... on UserAuthProviderArray {
                data {
                    provider
                    providerUserId
                    isActive
                    isDisabled
                    linkedAt
                    unlinkedAt
                    metadata
                }
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`;

export async function getUserAuthProvidersQuery() {
    const variables = {};
    const execute = await ApolloClientQuery(GET_USER_AUTH_PROVIDERS, variables, false);
    return execute;
}

const START_TELEGRAM_LINK = gql`
    mutation startTelegramLink($twoFactorChallengeId: String) {
        startTelegramLink(twoFactorChallengeId: $twoFactorChallengeId) {
            ... on ProviderLinkStart {
                url
                expiresAt
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

// polled while the client is in telegram, so it must not read a cached answer
export async function getUserAuthProvidersFreshQuery() {
    return await ApolloClientQuery(GET_USER_AUTH_PROVIDERS, {}, false, "network-only");
}

const START_WHATSAPP_LINK = gql`
    mutation startWhatsappLink($twoFactorChallengeId: String) {
        startWhatsappLink(twoFactorChallengeId: $twoFactorChallengeId) {
            ... on ProviderLinkStart {
                url
                expiresAt
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

export async function startWhatsappLinkMutation(twoFactorChallengeId = null) {
    return await ApolloClientQuery(START_WHATSAPP_LINK, {twoFactorChallengeId}, true);
}

export async function startTelegramLinkMutation(twoFactorChallengeId = null) {
    return await ApolloClientQuery(START_TELEGRAM_LINK, {twoFactorChallengeId}, true);
}

// unlike a provider link there is a ceremony to follow, so the challenge id comes back too
const START_LIVENESS_ENROLMENT = gql`
    mutation startLivenessEnrolment($twoFactorChallengeId: String) {
        startLivenessEnrolment(twoFactorChallengeId: $twoFactorChallengeId) {
            ... on LivenessEnrolmentStart {
                challengeId
                url
                expiresAt
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`;

export async function startLivenessEnrolmentMutation(twoFactorChallengeId = null) {
    return await ApolloClientQuery(START_LIVENESS_ENROLMENT, {twoFactorChallengeId}, true);
}

const START_USER_AUTH_PROVIDER_CONNECT = gql`
    mutation startUserAuthProviderConnect($input: StartUserAuthProviderConnectInput!) {
        startUserAuthProviderConnect(input: $input) {
            ... on StartProviderAuthPayload {
                authUrl
                authRequestId
                retryAfterSeconds
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`;

export async function startUserAuthProviderConnectMutation(input) {
    const session = createLogSession("Auth", "Provider", "startUserAuthProviderConnect");
    session.create({ description: "Starting provider connection", logData: { provider: input?.provider } });

    const variables = {
        input: {
            provider: input?.provider || null,
            twoFactorChallengeId: input?.twoFactorChallengeId || null,
            platform: input?.platform || "web",
            phoneNumber: input?.phoneNumber || null,
            clientOrigin: input?.clientOrigin || null,
            ipAddress: input?.ipAddress || null,
            userAgent: input?.userAgent || null,
        },
    };
    try {
        const execute = await ApolloClientQuery(START_USER_AUTH_PROVIDER_CONNECT, variables, true);
        session.create({ description: "Provider connection started", status: "info", logData: { provider: input?.provider } });
        return execute;
    } catch (e) {
        session.create({ description: "Provider connection failed", status: "error", logData: { provider: input?.provider, error: e } });
        throw e;
    }
}

const COMPLETE_USER_AUTH_PROVIDER_CONNECT = gql`
    mutation completeUserAuthProviderConnect($input: CompleteUserAuthProviderConnectInput!) {
        completeUserAuthProviderConnect(input: $input) {
            ... on CompleteProviderCallbackPayload {
                nextAction
                accessToken
                refreshToken
                signupToken
                linked
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`;

export async function completeUserAuthProviderConnectMutation(input) {
    const variables = {
        input: {
            provider: input?.provider || null,
            authRequestId: input?.authRequestId || null,
            code: input?.code || null,
            state: input?.state || null,
            otpCode: input?.otpCode || null,
            telegramData: input?.telegramData || null,
            ipAddress: input?.ipAddress || null,
            userAgent: input?.userAgent || null,
        },
    };
    const execute = await ApolloClientQuery(COMPLETE_USER_AUTH_PROVIDER_CONNECT, variables, true);
    return execute;
}

const DISCONNECT_USER_AUTH_PROVIDER = gql`
    mutation disconnectUserAuthProvider($input: UserAuthProviderActionInput!) {
        disconnectUserAuthProvider(input: $input) {
            ... on UserAuthProviderDisconnectPayload {
                success
                provider
                disconnectedAt
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

export async function disconnectUserAuthProviderMutation(input) {
    const session = createLogSession("Auth", "Provider", "disconnectUserAuthProvider");
    session.create({ description: "Disconnecting provider", logData: { provider: input?.provider } });

    const variables = {
        input: {
            provider: input?.provider || null,
            twoFactorChallengeId: input?.twoFactorChallengeId || null,
        },
    };
    try {
        const execute = await ApolloClientQuery(DISCONNECT_USER_AUTH_PROVIDER, variables, true);
        session.create({ description: "Provider disconnected", status: "info", logData: { provider: input?.provider } });
        return execute;
    } catch (e) {
        session.create({ description: "Provider disconnection failed", status: "error", logData: { provider: input?.provider, error: e } });
        throw e;
    }
}

// one round trip for the profile section: whether the brand offers face id at all, and
// whether this client already holds it
const GET_OWN_LIVENESS_STATUS = gql`
    query getOwnLivenessStatus {
        getOwnLivenessStatus {
            ... on OwnLivenessStatus {
                isOffered
                isEnrolled
                isDisabled
                enrolledAt
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

export async function getOwnLivenessStatusQuery() {
    // never the cache: the card is read straight after an enrolment changes the answer
    return await ApolloClientQuery(GET_OWN_LIVENESS_STATUS, {}, false, "network-only");
}
